// src/components/dashboard/OrgDashboard.tsx
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { SectionCard } from "@/components/common/SectionCard";
import { supabase } from "@/lib/supabase";
import { adminPost } from "@/lib/kbAdminClient";
import { LOCATIONS } from "@/config/locations";
import { fetchOrgLocations } from "@/lib/locations/fetchOrgLocations";
import {
  orgLocationPickerLabel,
  orgLocationPickerOptions,
  type OrgLocationPickerOption,
} from "@/lib/locations/orgLocationPickerOptions";
import { GhlHygieneCard } from "@/components/dashboard/GhlHygieneCard";
import { GhlSyncButton } from "@/components/dashboard/GhlSyncButton";
import { OrgDashboardHeaderSection } from "@/components/dashboard/OrgDashboardHeaderSection";
import {
  buildBreakdown,
  buildDailyBreakdown,
  fmtSource,
  toYMD,
  type DashboardMetrics,
  type DayRange,
  type LeadRow,
} from "@/components/dashboard/orgDashboardMetrics";
import {
  OrgDashboardHBar,
  OrgDashboardTooltip,
} from "@/components/dashboard/OrgDashboardUi";

export function OrgDashboard() {
  const auth = useAuth();
  const orgId = useScopedOrganizationId();

  const [days, setDays] = useState<DayRange>(30);
  const [orgName, setOrgName] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [locationId, setLocationId] = useState<string | null>(auth.activeLocationId ?? null);
  const [locationSaving, setLocationSaving] = useState(false);
  const [kbGapCount, setKbGapCount] = useState<number | null>(null);
  const [locationOptions, setLocationOptions] = useState<OrgLocationPickerOption[]>(() =>
    orgLocationPickerOptions([], LOCATIONS)
  );

  useEffect(() => {
    if (!orgId || !supabase) {
      setLocationOptions(orgLocationPickerOptions([], LOCATIONS));
      return;
    }
    let alive = true;
    void fetchOrgLocations(supabase, orgId).then(({ data }) => {
      if (!alive) return;
      setLocationOptions(orgLocationPickerOptions(data.map((r) => ({ id: r.id, name: r.name, city: r.city })), LOCATIONS));
    });
    return () => {
      alive = false;
    };
  }, [orgId]);

  useEffect(() => {
    void adminPost<{ ok: true; openCount: number }>("/api/kb/admin/getGaps", { countOnly: true }).then((r) => {
      if (r.ok) setKbGapCount(Number(r.data.openCount || 0));
    });
  }, [orgId]);

  const setMyLocation = async (locId: string | null) => {
    if (!supabase) return;
    setLocationSaving(true);
    try {
      await supabase.rpc("set_my_active_location", { p_location_id: locId });
      setLocationId(locId);
    } catch (e) {
      console.warn("[dashboard] set_my_active_location failed", e);
    } finally {
      setLocationSaving(false);
    }
  };

  const fetchData = useCallback(async () => {
    if (!orgId || !supabase) {
      setLoading(false);
      return;
    }
    const db = supabase;
    setLoading(true);
    setError(null);

    try {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);
      const cutoffIso = cutoff.toISOString();

      const [leadsRes, membersRes, orgRes] = await Promise.all([
        db
          .from("leads")
          .select("id, status, created_at, data")
          .eq("organization_id", orgId)
          .eq("is_test", false)
          .gte("created_at", cutoffIso)
          .order("created_at", { ascending: true }),
        db.from("user_roles").select("id").eq("org_id", orgId).eq("is_active", true),
        db.from("organizations").select("name").eq("id", orgId).maybeSingle(),
      ]);

      if (leadsRes.error) throw new Error(leadsRes.error.message);
      if (membersRes.error) throw new Error(membersRes.error.message);

      if (!orgRes.error && orgRes.data) setOrgName((orgRes.data as { name?: string }).name ?? null);

      const rows = (leadsRes.data ?? []) as LeadRow[];
      const todayStr = toYMD(new Date().toISOString());
      const todayRows = rows.filter((r) => toYMD(r.created_at) === todayStr);

      const isSent = (r: LeadRow) => r.status === "sent" || r.status === "archived";

      setMetrics({
        totalLeads: rows.length,
        sentLeads: rows.filter(isSent).length,
        draftLeads: rows.filter((r) => r.status === "draft").length,
        todayLeads: todayRows.length,
        todaySent: todayRows.filter(isSent).length,
        activeMembers: (membersRes.data ?? []).length,
        dailyBreakdown: buildDailyBreakdown(rows, days),
        sourceBreakdown: buildBreakdown(rows, (r) => r.data?.extracted?.lead_source_category ?? null).map((e) => ({
          ...e,
          label: fmtSource(e.label),
        })),
        productBreakdown: buildBreakdown(rows, (r) => r.data?.extracted?.product_line ?? null),
      });
    } catch (e: unknown) {
      setError((e as Error)?.message ?? "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [orgId, days]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const sentRate =
    metrics && metrics.totalLeads > 0 ? Math.round((metrics.sentLeads / metrics.totalLeads) * 100) : null;

  // Container/padding doctrine \u00a7K2: secondary cards adopt SectionCard. The
  // rich-title header card lives in OrgDashboardHeaderSection (extracted to
  // keep this file under the 300-line hard limit).
  return (
    <div className="w-full text-left space-y-4">
      <OrgDashboardHeaderSection
        auth={auth}
        orgName={orgName}
        orgId={orgId}
        locationId={locationId}
        locationOptions={locationOptions}
        locationSaving={locationSaving}
        setMyLocation={setMyLocation}
        days={days}
        setDays={setDays}
        loading={loading}
        fetchData={fetchData}
        error={error}
        metrics={metrics}
        kbGapCount={kbGapCount}
        sentRate={sentRate}
      />

      {metrics && (metrics.sourceBreakdown.length > 0 || metrics.productBreakdown.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {metrics.sourceBreakdown.length > 0 && (
            <SectionCard
              title="Lead source"
              rightSlot={<OrgDashboardTooltip text="Lead source distribution for app-submitted leads only. Source is captured from the lead intake form." />}
            >
              <div className="space-y-2">
                {metrics.sourceBreakdown.map((e) => (
                  <OrgDashboardHBar
                    key={e.label}
                    label={e.label}
                    count={e.count}
                    max={metrics.sourceBreakdown[0]?.count ?? 1}
                    color="bg-indigo-400"
                  />
                ))}
              </div>
            </SectionCard>
          )}

          {metrics.productBreakdown.length > 0 && (
            <SectionCard
              title="Product type"
              rightSlot={<OrgDashboardTooltip text="Product type distribution for app-submitted leads only. Pulled from the extracted lead data." />}
            >
              <div className="space-y-2">
                {metrics.productBreakdown.map((e) => (
                  <OrgDashboardHBar
                    key={e.label}
                    label={e.label}
                    count={e.count}
                    max={metrics.productBreakdown[0]?.count ?? 1}
                    color="bg-amber-400"
                  />
                ))}
              </div>
            </SectionCard>
          )}
        </div>
      )}

      {orgId && (
        <div className="flex items-center gap-3 pt-1 flex-wrap">
          <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 whitespace-nowrap">
            CRM Health — GoHighLevel
          </span>
          <OrgDashboardTooltip text="Pipeline data pulled directly from your CRM. Use the sync button to refresh. Configure connections in Admin → GHL Settings." />
          <div className="flex-1 h-px bg-gray-100" />
          <GhlSyncButton orgId={orgId} />
        </div>
      )}

      {orgId && (
        <GhlHygieneCard
          orgId={orgId}
          title={locationId ? "CRM Pipeline Hygiene — Active Location" : "CRM Pipeline Hygiene — All Pipelines"}
          locationFilterId={locationId}
          locationFilterName={locationId ? orgLocationPickerLabel(locationOptions, locationId) : null}
        />
      )}
    </div>
  );
}
